import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { auth, getToken, setToken, clearToken } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Decode JWT payload
const decodeToken = (token) => {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
};

// Build user profile from token or API response
const buildUser = (data, token) => {
  const payload = decodeToken(token);
  const source = data || payload;

  // SECURITY: Require valid source data - never default to admin
  if (!source || !source.username) {
    return null;
  }

  return {
    id: source.user_id || source.id || '',
    username: source.username || source.user,
    role: source.role || 'viewer', // Default to least privilege
    email: source.email || '',
    full_name: source.full_name || source.username || '',
    is_active: source.is_active ?? true,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Validate existing token on mount
  useEffect(() => {
    const token = getToken();
    if (token) {
      auth.getMe()
        .then((res) => {
          const data = res.data?.data || res.data;
          const builtUser = buildUser(data, token);
          if (builtUser) {
            setUser(builtUser);
          } else {
            // Invalid user data - clear everything
            clearToken();
            setUser(null);
          }
        })
        .catch(() => {
          // SECURITY: Token validation failed - clear everything and require re-login
          clearToken();
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      setError(null);
      setLoading(true);

      const res = await auth.login(username, password);
      const data = res.data?.data || res.data;
      const { token, user: userData } = data;

      if (!token) throw new Error('No token received');

      setToken(token);
      localStorage.setItem('sessionExpiry', String(Date.now() + 8 * 60 * 60 * 1000));
      setUser(buildUser(userData, token));

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Login failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await auth.logout();
    clearToken();
    setUser(null);
    setError(null);
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      await auth.changePassword(currentPassword, newPassword);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Failed' };
    }
  }, []);

  // Auto-refresh: check token expiry every 60s, extend session if < 5min remaining
  const refreshRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    refreshRef.current = setInterval(() => {
      const token = getToken();
      if (!token) return;
      const payload = decodeToken(token);
      if (!payload?.exp) return;
      const remaining = payload.exp * 1000 - Date.now();
      if (remaining < 5 * 60 * 1000 && remaining > 0) {
        // Token expiring soon — re-validate to keep session alive
        auth.getMe().then((res) => {
          const data = res.data?.data || res.data;
          if (data?.token) {
            setToken(data.token);
            localStorage.setItem('sessionExpiry', String(Date.now() + 8 * 60 * 60 * 1000));
          }
        }).catch(() => {});
      } else if (remaining <= 0) {
        clearToken();
        setUser(null);
      }
    }, 60000);
    return () => clearInterval(refreshRef.current);
  }, [user]);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    changePassword,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
